import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { Location, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Button from '../../components/button/component';
import Accordion from '../../components/accordion/component';
import RiskScore from '../../components/risk-score/component';
import RiskIcon from '../../theme/styled-components/RiskIcon';
import invariant from '../../utils/invariant';

const useStyles = makeStyles((theme) => ({
  accordion: {
    backgroundColor: '#121F32',
    borderRadius: 0,
    boxShadow: 'none',
    marginTop: 35,
    padding: '15px 0',
    [theme.breakpoints.up('sm')]: {
      padding: 15,
    },
  },
  accordionContent: {
    borderTop: '1px solid rgba(216,216,216,.3)',
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  accordionTitle: {
    fontSize: theme.typography.pxToRem(24),
  },
  content: {
    padding: '16px 0',
    textAlign: 'center',
  },
  description: {
    marginBottom: 45,
  },
  panel: {
    backgroundColor: theme.palette.common.backgroundHighlight,
  },
  score: {
    fontSize: theme.typography.pxToRem(45),
  },
  title: {
    fontSize: theme.typography.pxToRem(55),
    marginBottom: 35,
    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.pxToRem(34),
      marginBottom: 25,
    },
  },
}));

const risk = [
  {
    id: 'lower',
    rating: 'Lower',
    score: 1,
    description:
      "You're likely to be more conservative with your investments and understand there may be some short-term changes in value to get potentially modest or relatively stable returns.",
  },
  {
    id: 'lower-to-medium',
    rating: 'Lower to Medium',
    score: 2,
    description:
      "You're relatively cautious with your investments. You want the potential of getting reasonable long-term returns and understand there may be some frequent but small changes in the value of your investments.",
  },
  {
    id: 'medium',
    rating: 'Medium',
    score: 3,
    description:
      "You have a balanced approach to risk. You don't look for risky investments but you don't avoid them either. You're prepared to accept fluctuations in the value of your investments to try and get potentially better long-term returns. You understand that the value of your investments might change frequently and sometimes significantly.",
  },
  {
    id: 'medium-to-higher',
    rating: 'Medium to Higher',
    score: 4,
    description:
      "You're comfortable with investment risk. You want to get potentially higher long-term returns and understand that the value of your investments is likely to change frequently and often significantly.",
  },
  {
    id: 'higher',
    rating: 'Higher',
    score: 5,
    description:
      "You're very comfortable with investment risk. You're aiming for potentially high long-term returns and are less concerned if the value of your investments go up and down over the short or medium term. You understand that the value of your investments is likely to change very frequently and significantly.",
  },
];

interface ResultsLocation extends Location {
  state: {
    score?: number;
  };
}

interface ResultsProps {
  score?: number;
}

function Results(props: ResultsProps) {
  const classes = useStyles();

  const { state } = useLocation() as ResultsLocation;

  const navigate = useNavigate();

  const { score: propsScore } = props;

  const riskScore = propsScore ?? state.score;

  invariant(riskScore);

  const result = risk.find(({ score }) => score === riskScore);

  if (!result) {
    return <Navigate to="/atr/questionnaire" />;
  }

  return (
    <Grid container>
      <Container maxWidth="lg">
        <Typography className={classes.title} align="center" variant="h1">
          Risk profiling
        </Typography>

        <Card className={classes.panel}>
          <CardContent className={classes.content}>
            <Container maxWidth="sm">
              <Typography component="p" variant="subtitle1">
                Based on your answers
              </Typography>

              <Typography className={classes.score} variant="h2">
                Risk level {result.score} - {result.rating}
              </Typography>

              <RiskScore maximum={5} score={result.score} />

              <Typography className={classes.description}>
                {result.description}
              </Typography>

              {state && (
                <Button
                  onClick={() => {
                    navigate('/actions');
                  }}
                >
                  Back to my actions
                </Button>
              )}
            </Container>
          </CardContent>
        </Card>

        <Card className={classes.accordion}>
          <CardHeader
            avatar={<RiskIcon />}
            title="Risk level categories"
            titleTypographyProps={{
              className: classes.accordionTitle,
              variant: 'body2',
            }}
          />

          <CardContent className={classes.accordionContent}>
            <Accordion
              items={risk.map(({ description, id, rating, score }) => ({
                id,
                title: `${score}. ${rating}`,
                content: <Typography>{description}</Typography>,
              }))}
            />
          </CardContent>
        </Card>
      </Container>
    </Grid>
  );
}

export default Results;
